import { action, computed, observable, runInAction } from "mobx";
import moment from "moment";
import { FormStore } from "@modules/formx";
import { isEmpty } from "lodash";
import {
  ConsultantEventLog_GetEvents as getConsultantEvents,
  ConsultantProfileForQualityItem_GetConsultant as getConsultant,
  ConsultantProfileForQualityItemFragment,
  ConsultantQualityItemEditor_ClearStatus as clearStatus,
  ConsultantQualityItemEditor_Get as getData,
  ConsultantQualityItemEditor_SetApproved as setApproved,
  ConsultantQualityItemEditor_SetBooked as setBooked,
  ConsultantQualityItemEditor_SetOrdered as setOrdered,
  ConsultantQualityItemEditor_SetConsultantResponsible as setConsultantResponsible,
  ConsultantQualityItemEditor_Upload as upload,
  ConsultantQualityItemEditorFragment,
  ConsultantSearchCard_Get
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { FormData, schema, statusToMeta } from "../../quality-item-editor/form";
import { getUtcDate } from "../../quality-item-editor";
import { Snackbar } from "@bonliva-crm/ui";

type Data = ConsultantQualityItemEditorFragment;

export class HlrQualityCheckItemEditorStore {
  @observable data: Data;
  @observable consultant: ConsultantProfileForQualityItemFragment = null;
  @observable error = null;
  @observable loading = true;
  @observable isDocumentRequired = false;
  @observable withDocument = false;
  @observable fileToUpload: File = null;

  @observable metadata: any = {};
  @observable metaCanSubmit = false;

  canApprove: boolean = false;

  @computed get selectedScheme() {
    return schema;
  }

  @computed get name() {
    return this.data?.Name;
  }

  form = new FormStore<FormData>({ schema: this.selectedScheme, initialDirty: true });

  constructor(
    private consultantId: string,
    private country: string,
    private itemName: string,
    private onClose: () => void,
    public snackbar: Snackbar
  ) {
    this.fetch();
    this.fetchConsultant();

    this.canApprove = store().user.inTeam("Kvalitetsavdelningen");
    (window as any).hlrQualityCheckItemEditorStore = this;
  }

  get availableStatusNames() {
    return this.data ? this.data.AvailableStatuses.map((x) => x.Name) : [];
  }

  get selectedStatusIndex() {
    const result = this.form.values.selectedStatus
      ? this.data.AvailableStatuses.findIndex(
          (x) => x.Name === this.form.values.selectedStatus
        )
      : 0;

    return result;
  }

  get statusOptions() {
    return this.data?.AvailableStatuses[this.selectedStatusIndex];
  }

  get canSubmit() {
    return (this.form.canSubmit || this.metaCanSubmit) && !this.isDocumentRequired;
  }

  get canCancel() {
    return Boolean(this.data?.Status);
  }

  requestUpload = () =>
    store()
      .upload.requestFileUpload({
        multiple: false
      })
      .then((files) => {
        this.fileToUpload = files[0];
      });

  handleUpload = async () => {
    if (!this.fileToUpload || !this.form.values.selectedStatus) {
      return;
    }

    this.loading = true;

    return upload
      .func({
        consultantId: this.consultantId,
        documentType: this.data.DocumentType,
        statusName: this.form.values.selectedStatus,
        file: this.fileToUpload
      })
      .then(() => this.fetch())
      .catch((err) => {
        this.snackbar.showError(`Uploading failed\nReason: ${err}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  closeUpload = () => (this.fileToUpload = null);

  submitApproved = () =>
    this.submit(
      setApproved.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date,
          comment: this.metadata.comment
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );

  submitOrdered = () => {
    this.submit(
      setOrdered.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  submitBooked = () => {
    this.submit(
      setBooked.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  submitConsultantResponsible = () => {
    this.submit(
      setConsultantResponsible.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  clearStatus = () => {
    this.submit(
      clearStatus.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  @action setStatus = async (statusName: string) => {
    if (!statusName) {
      return;
    }

    const status = this.data?.AvailableStatuses.find((x) => x.Name === statusName);
    // if (!status && !this.referenceId) {
    if (!status) {
      throw new Error(`Cannot find status ${status.Name}`);
    }

    this.form.setValue("selectedStatus", status.Name);

    if (status.Name === "RequestSent") {
      this.form.setValue("date", new Date());
    }
  };

  checkStatus = (statusName: string) => {
    if (!statusName) {
      return {
        disabled: false
      };
    }

    const status = this.data?.AvailableStatuses.find((x) => x.Name === statusName);

    return {
      disabled:
        status && status.WithDocument && status.IsDocumentRequired
          ? !this.fileToUpload
          : false
    };
  };

  setStatusMetaData(metadata: any) {
    this.metadata = metadata;
  }

  setStatusCanSubmit(canSubmit: boolean) {
    this.metaCanSubmit = canSubmit;
  }

  private fetch = (refetch = false) => {
    this.loading = true;

    return getData
      .func(
        {
          consultantId: this.consultantId,
          country: this.country,
          itemName: this.itemName
        },
        { fetchPolicy: "network-only" }
      )
      .then(({ data }) => {
        runInAction(() => {
          const prevStatus = this.form.values.selectedStatus;
          this.setData(data.getConsultantQualityItem);
          this.setStatus(prevStatus);
        });
      })
      .catch((err) => {
        this.snackbar.showError(`Cannot fetch quality item`);
        console.error(err);
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      });
  };

  @action private setData = (data: ConsultantQualityItemEditorFragment) => {
    this.data = data;
    this.withDocument = this.data.AvailableStatuses.some((x) => x.WithDocument);

    // if there is a status - date should be treat as initial value
    // otherwise is just value (form is dirty)
    if (this.data.Status) {
      this.form.reset({
        date: moment(
          this.form.dirty && this.form.values.selectedStatus === this.data.Status.Name
            ? this.form.values.date
            : getUtcDate(this.data.Status.SetOn)
        ).toDate(),
        selectedStatus: this.data.Status.Name
      });

      this.metadata = statusToMeta(this.data.Status);
    } else {
      this.form.setValue("date", this.form.dirty ? this.form.values.date : getUtcDate());

      if (!isEmpty(this.data.AvailableStatuses)) {
        this.setStatus(this.data.AvailableStatuses[0].Name);
      }
    }
  };

  private submit = (promise: Promise<any>) => {
    this.loading = true;

    promise
      .then(() => {
        this.snackbar.showSuccess(locale.Saved);
        this.onClose();
      })
      .catch((err) => {
        this.snackbar.showError(`Cannot save.\nReason: ${err}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  private fetchConsultant() {
    getConsultant
      .func({
        id: this.consultantId
      })
      .then(({ data }) => {
        this.consultant = data.consultantById;
      })
      .catch((e) => console.log(e));
  }
}
